/**
 * Currency Utilities
 *
 * Centralized currency formatting functions with locale and currency support.
 */

export type CurrencyCode = 'EUR' | 'USD' | 'GBP' | 'PLN' | 'CHF'

export interface CurrencyOption {
  code: CurrencyCode
  symbol: string
  name: string
  flag: string
}

export const currencyOptions: CurrencyOption[] = [
  { code: 'EUR', symbol: '\u20AC', name: 'Euro', flag: '\uD83C\uDDEA\uD83C\uDDFA' },
  { code: 'USD', symbol: '$', name: 'US Dollar', flag: '\uD83C\uDDFA\uD83C\uDDF8' },
  { code: 'GBP', symbol: '\u00A3', name: 'British Pound', flag: '\uD83C\uDDEC\uD83C\uDDE7' },
  { code: 'PLN', symbol: 'z\u0142', name: 'Polish Zloty', flag: '\uD83C\uDDF5\uD83C\uDDF1' },
  { code: 'CHF', symbol: 'Fr.', name: 'Swiss Franc', flag: '\uD83C\uDDE8\uD83C\uDDED' },
]

/**
 * Map language codes to locale codes for Intl formatting
 */
const languageToLocale: Record<string, string> = {
  en: 'en-US',
  de: 'de-DE',
  fr: 'fr-FR',
  es: 'es-ES',
  it: 'it-IT',
  pl: 'pl-PL',
}

/**
 * Format currency amount with locale-aware number formatting
 *
 * @param amount - The numeric amount to format
 * @param currencyCode - ISO 4217 currency code (e.g., 'USD', 'EUR')
 * @param language - Language code for number formatting (e.g., 'en', 'de')
 */
export function formatCurrency(
  amount: number,
  currencyCode: string = 'EUR',
  language: string = 'en'
): string {
  const locale = languageToLocale[language] || 'en-US'
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
  }).format(amount)
}

/**
 * Get currency symbol for display
 */
export function getCurrencySymbol(currencyCode: CurrencyCode): string {
  const currency = currencyOptions.find((c) => c.code === currencyCode)
  return currency?.symbol || currencyCode
}

/**
 * Get default currency based on language
 */
export function getDefaultCurrencyForLanguage(language: string): CurrencyCode {
  const languageCurrencyMap: Record<string, CurrencyCode> = {
    en: 'USD',
    de: 'EUR',
    fr: 'EUR',
    es: 'EUR',
    it: 'EUR',
    pl: 'PLN',
  }
  return languageCurrencyMap[language] || 'EUR'
}
